import React, { useState, useEffect } from 'react';
import { WebhookSignal, WebhookHistory } from '../types/webhook';
import { webhookService } from '../services/webhookService';

interface SignalCardProps {
  onSignalClick?: (signal: WebhookSignal) => void;
  onClose?: () => void;
}

const SignalCard = ({ onSignalClick, onClose }: SignalCardProps) => {
  const [history, setHistory] = useState<WebhookHistory>(webhookService.getHistory());
  const [selectedSignal, setSelectedSignal] = useState<WebhookSignal | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showAllSignals, setShowAllSignals] = useState(false);

  useEffect(() => {
    // Listen for new signals
    const removeListener = webhookService.addListener((signal) => {
      setHistory(webhookService.getHistory());
    });

    return removeListener;
  }, []);

  const formatPrice = (price?: number) => {
    if (!price) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 8
    }).format(price);
  };

  const formatTimestamp = (timestamp: number) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const formatTime = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  const getSignalIcon = (type: string) => {
    switch (type) {
      case 'buy': return '📈';
      case 'sell': return '📉';
      case 'alert': return '⚠️';
      case 'update': return '🔄';
      default: return '📊';
    }
  };

  const getSignalColor = (type: string) => {
    switch (type) {
      case 'buy': return '#10b981';
      case 'sell': return '#ef4444';
      case 'alert': return '#f59e0b';
      case 'update': return '#3b82f6';
      default: return '#6b7280';
    }
  };

  const getConfidenceColor = (confidence?: number) => {
    if (!confidence) return '#6b7280';
    if (confidence >= 0.8) return '#10b981';
    if (confidence >= 0.6) return '#f59e0b';
    return '#ef4444';
  };

  const handleSignalClick = (signal: WebhookSignal) => {
    setSelectedSignal(signal);
    setShowDetails(true);
    if (onSignalClick) {
      onSignalClick(signal);
    }
  };

  const recentSignals = showAllSignals 
    ? history.signals.slice().reverse() 
    : history.signals.slice(-10).reverse();

  return (
    <div className="signal-card">
      <div className="signal-card-header">
        <div className="signal-stats">
          <span className="total-signals">Total: {history.totalReceived}</span>
          <span className="showing-signals">Showing: {recentSignals.length}</span>
          <span className="last-update">Last: {formatTimestamp(history.lastReceived)}</span>
        </div>
        <div className="signal-header-actions">
          <button 
            className="toggle-signals-btn"
            onClick={() => setShowAllSignals(!showAllSignals)}
            title={showAllSignals ? "Show recent signals only" : "Show all signals"}
          >
            {showAllSignals ? "Recent Only" : "Show All"}
          </button>
          {onClose && (
            <button 
              className="close-btn"
              onClick={onClose}
              title="Close Signal Card"
            >
              ✖
            </button>
          )}
        </div>
      </div>

      <div className="signal-list">
        {recentSignals.length === 0 ? (
          <div className="no-signals">
            <div className="no-signals-icon">📊</div>
            <div className="no-signals-text">Waiting for signals...</div>
            <div className="no-signals-subtext">Signals will appear here automatically</div>
          </div>
        ) : (
          recentSignals.map((signal) => (
            <div
              key={signal.id}
              className="signal-item"
              onClick={() => handleSignalClick(signal)}
              style={{ borderLeftColor: getSignalColor(signal.type) }}
            >
              <div className="signal-item-header">
                <div className="signal-type">
                  <span className="signal-icon">{getSignalIcon(signal.type)}</span>
                  <span className="signal-type-text">{signal.type.toUpperCase()}</span>
                </div>
                <div className="signal-time">
                  <div className="signal-time-relative">{formatTimestamp(signal.timestamp)}</div>
                  <div className="signal-time-absolute">{formatTime(signal.timestamp)}</div>
                </div>
              </div>

              <div className="signal-symbol">{signal.symbol}</div>

              {signal.price && (
                <div className="signal-price">
                  {formatPrice(signal.price)}
                </div>
              )}

              {/* Trading Levels for Buy Signals */}
              {signal.type === 'buy' && (signal.buyAt || signal.takeProfit || signal.stopLoss) && (
                <div className="trading-levels">
                  {signal.buyAt && (
                    <div className="trading-level buy-level">
                      <span className="level-label">Buy At:</span>
                      <span className="level-price">{formatPrice(signal.buyAt)}</span>
                    </div>
                  )}
                  {signal.takeProfit && (
                    <div className="trading-level profit-level">
                      <span className="level-label">TP:</span>
                      <span className="level-price">{formatPrice(signal.takeProfit)}</span>
                    </div>
                  )}
                  {signal.stopLoss && (
                    <div className="trading-level loss-level">
                      <span className="level-label">SL:</span>
                      <span className="level-price">{formatPrice(signal.stopLoss)}</span>
                    </div>
                  )}
                </div>
              )}

              {signal.message && (
                <div className="signal-message">{signal.message}</div>
              )}

              <div className="signal-meta">
                <div className="signal-source">{signal.source}</div>
                {signal.confidence && (
                  <div 
                    className="signal-confidence"
                    style={{ color: getConfidenceColor(signal.confidence) }}
                  >
                    {(signal.confidence * 100).toFixed(0)}% confidence
                  </div>
                )}
              </div>

              {signal.metadata && (
                <div className="signal-metadata">
                  {signal.metadata.rsi && (
                    <span className="meta-item">RSI: {signal.metadata.rsi}</span>
                  )}
                  {signal.metadata.macd && (
                    <span className="meta-item">MACD: {signal.metadata.macd}</span>
                  )}
                  {signal.metadata.volume && (
                    <span className="meta-item">Vol: {signal.metadata.volume.toLocaleString()}</span>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {showDetails && selectedSignal && (
        <div className="signal-details-overlay" onClick={() => setShowDetails(false)}>
          <div className="signal-details" onClick={(e) => e.stopPropagation()}>
            <div className="signal-details-header">
              <h4>Signal Details</h4>
              <button 
                className="close-button"
                onClick={() => setShowDetails(false)}
              >
                ×
              </button>
            </div>

            <div className="signal-details-content">
              <div className="detail-section">
                <h5>Basic Information</h5>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Type:</label>
                    <span style={{ color: getSignalColor(selectedSignal.type) }}>
                      {selectedSignal.type.toUpperCase()}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>Symbol:</label>
                    <span>{selectedSignal.symbol}</span>
                  </div>
                  <div className="detail-item">
                    <label>Timeframe:</label>
                    <span>{selectedSignal.timeframe || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Source:</label>
                    <span>{selectedSignal.source}</span>
                  </div>
                  <div className="detail-item">
                    <label>Timestamp:</label>
                    <span>{new Date(selectedSignal.timestamp).toLocaleString()}</span>
                  </div>
                  <div className="detail-item">
                    <label>Price:</label>
                    <span>{formatPrice(selectedSignal.price)}</span>
                  </div>
                </div>
              </div>

              {/* Trading Levels Section */}
              {selectedSignal.type === 'buy' && (selectedSignal.buyAt || selectedSignal.takeProfit || selectedSignal.stopLoss) && (
                <div className="detail-section">
                  <h5>Trading Levels</h5>
                  <div className="detail-grid">
                    {selectedSignal.buyAt && (
                      <div className="detail-item">
                        <label>Buy At:</label>
                        <span style={{ color: '#10b981', fontWeight: '600' }}>
                          {formatPrice(selectedSignal.buyAt)}
                        </span>
                      </div>
                    )}
                    {selectedSignal.takeProfit && (
                      <div className="detail-item">
                        <label>Take Profit:</label>
                        <span style={{ color: '#f97316', fontWeight: '600' }}>
                          {formatPrice(selectedSignal.takeProfit)}
                        </span>
                      </div>
                    )}
                    {selectedSignal.stopLoss && (
                      <div className="detail-item">
                        <label>Stop Loss:</label>
                        <span style={{ color: '#ef4444', fontWeight: '600' }}>
                          {formatPrice(selectedSignal.stopLoss)}
                        </span>
                      </div>
                    )}
                    {selectedSignal.buyAt && selectedSignal.takeProfit && selectedSignal.stopLoss && (
                      <div className="detail-item">
                        <label>Risk/Reward:</label>
                        <span style={{ color: '#f1f5f9', fontWeight: '600' }}>
                          {((selectedSignal.takeProfit - selectedSignal.buyAt) / (selectedSignal.buyAt - selectedSignal.stopLoss)).toFixed(2)}:1
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {selectedSignal.message && (
                <div className="detail-section">
                  <h5>Message</h5>
                  <p>{selectedSignal.message}</p>
                </div>
              )}

              {selectedSignal.confidence && (
                <div className="detail-section">
                  <h5>Confidence</h5>
                  <div className="confidence-bar">
                    <div 
                      className="confidence-fill"
                      style={{ 
                        width: `${selectedSignal.confidence * 100}%`,
                        backgroundColor: getConfidenceColor(selectedSignal.confidence)
                      }}
                    />
                    <span className="confidence-text">
                      {(selectedSignal.confidence * 100).toFixed(1)}%
                    </span>
                  </div>
                </div>
              )}

              {selectedSignal.metadata && Object.keys(selectedSignal.metadata).length > 0 && (
                <div className="detail-section">
                  <h5>Technical Data</h5>
                  <div className="metadata-grid">
                    {Object.entries(selectedSignal.metadata).map(([key, value]) => (
                      <div key={key} className="metadata-item">
                        <label>{key.toUpperCase()}:</label>
                        <span>{typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SignalCard;
